import { useViewjobProctorAllocation } from "../../proctor/__generated__/ViewjobProctorContext";
import { AfsauthRajRequireLoginTst } from "../../proctor/__generated__/ViewjobProctorSpecifications";

import { useMemo } from "react";

export interface AfsauthRajRequireLoginPayload {
  exemptCountries?: string[];
}

function parseAfsauthRajRequireLoginPayload(
  payloadJson: string | null,
): AfsauthRajRequireLoginPayload | null {
  if (!payloadJson) {
    return null;
  }

  try {
    let parsed: unknown = payloadJson;
    let maxParseCount = 2;

    // The proctor payload is stringified twice on Android but only once on iOS.
    while (typeof parsed === "string" && maxParseCount > 0) {
      parsed = JSON.parse(parsed);
      maxParseCount--;
    }

    if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      const exemptCountries = (parsed as Record<string, unknown>).exemptCountries;
      // Reject the payload if `exemptCountries` is present but isn't a
      // string array: fail closed so the gate stays active rather than
      // crashing later in `isCountryExempt`.
      if (
        exemptCountries !== undefined &&
        (!Array.isArray(exemptCountries) || !exemptCountries.every((c) => typeof c === "string"))
      ) {
        return null;
      }
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      return parsed as AfsauthRajRequireLoginPayload;
    }
  } catch {
    // Fall through to null on malformed JSON.
  }

  return null;
}

/**
 * Returns whether the AFSAUTH-3425 "require login for RAJ" gate should be
 * applied to the current job. The gate is active when the user is in the
 * `test` bucket AND the job's country is not on the proctor payload's
 * `exemptCountries` list.
 */
export function useIsAfsauthRajRequireLoginActiveForJob(jobCountry: string | undefined): boolean {
  const { value, payloadJson } = useViewjobProctorAllocation(AfsauthRajRequireLoginTst);
  const payload = useMemo(() => parseAfsauthRajRequireLoginPayload(payloadJson), [payloadJson]);

  const isInTestBucket = value === AfsauthRajRequireLoginTst.buckets.Test;

  const exemptCountries = payload?.exemptCountries;
  const isCountryExempt =
    jobCountry != null &&
    Array.isArray(exemptCountries) &&
    exemptCountries.some((c) => c.toUpperCase() === jobCountry.toUpperCase());

  return isInTestBucket && !isCountryExempt;
}
